import React, { useEffect, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Chip,
  CircularProgress,
  Alert,
  LinearProgress,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import { ExpandMore, Lock, CheckCircle, Cancel, RemoveCircleOutline } from '@mui/icons-material';
import {
  fetchSessionReview,
  QuestionReview,
  SessionReview,
  SessionReviewResult
} from '../services/sessionReviewService';

/**
 * Paid post-mock review (monetization feature #01) — per-question explanations plus
 * the weak-area breakdown by cognitive skill.
 *
 * Renders three distinct states, which is the point of the feature: unlocked (the
 * review), locked (the upsell, on a `402` from the entitlement gate), and error.
 * "Locked" is a product state, not a failure, and must never look like one.
 */

interface Props {
  sessionId: string;
  onUpgradeClick?: () => void;
}

/** Green above 70%, amber 40-70, red below — the weak areas read at a glance. */
const accuracyColor = (pct: number): 'success' | 'warning' | 'error' =>
  pct >= 70 ? 'success' : pct >= 40 ? 'warning' : 'error';

const QuestionRow: React.FC<{ q: QuestionReview; n: number }> = ({ q, n }) => {
  const verdict = !q.attempted
    ? { icon: <RemoveCircleOutline fontSize="small" />, label: 'Skipped', color: 'default' as const }
    : q.correct
      ? { icon: <CheckCircle fontSize="small" />, label: 'Correct', color: 'success' as const }
      : { icon: <Cancel fontSize="small" />, label: 'Incorrect', color: 'error' as const };

  return (
    <Accordion disableGutters sx={{ '&:before': { display: 'none' }, mb: 1, borderRadius: 1 }}>
      <AccordionSummary expandIcon={<ExpandMore />}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%', pr: 1 }}>
          <Typography sx={{ fontWeight: 700, minWidth: 28 }}>{n}.</Typography>
          <Typography
            sx={{
              flex: 1,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}
          >
            {q.question ?? 'Question unavailable'}
          </Typography>
          <Chip size="small" icon={verdict.icon} label={verdict.label} color={verdict.color} />
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        {q.cognitiveSkill && (
          <Chip size="small" variant="outlined" label={q.cognitiveSkill} sx={{ mb: 1.5 }} />
        )}

        {q.options && q.options.length > 0 && (
          <Box sx={{ mb: 2 }}>
            {q.options.map((opt, i) => {
              const isCorrect = q.correctAnswerIndex === i;
              const isChosen = q.studentAnswerIndex === i;
              return (
                <Box
                  key={i}
                  sx={{
                    px: 1.5,
                    py: 0.75,
                    mb: 0.5,
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: isCorrect ? 'success.main' : isChosen ? 'error.main' : 'divider',
                    bgcolor: isCorrect
                      ? 'success.light'
                      : isChosen
                        ? 'error.light'
                        : 'transparent'
                  }}
                >
                  <Typography variant="body2">
                    {opt}
                    {isCorrect && <b> ✓ correct answer</b>}
                    {isChosen && !isCorrect && <b> ← your answer</b>}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        )}

        {/* The explanation is the thing being sold — give it visual weight. */}
        {q.explanation ? (
          <Box sx={{ p: 1.5, borderRadius: 1, bgcolor: 'action.hover' }}>
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
              Why
            </Typography>
            <Typography variant="body2">{q.explanation}</Typography>
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary">
            No explanation recorded for this question.
          </Typography>
        )}

        {q.hints && q.hints.length > 0 && (
          <Box sx={{ mt: 1.5 }}>
            <Typography variant="subtitle2">Hints</Typography>
            {q.hints.map((h, i) => (
              <Typography key={i} variant="body2" color="text.secondary">
                • {h}
              </Typography>
            ))}
          </Box>
        )}

        {typeof q.timeTakenSeconds === 'number' && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
            Time spent: {q.timeTakenSeconds}s
          </Typography>
        )}
      </AccordionDetails>
    </Accordion>
  );
};

const WeakAreas: React.FC<{ review: SessionReview }> = ({ review }) => {
  if (!review.skillBreakdown?.length) return null;
  // The service returns these weakest-first, so no re-sorting here.
  const weakest = review.skillBreakdown[0];
  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="h6" sx={{ mb: 0.5 }}>
        Where you lost marks
      </Typography>
      {weakest && weakest.accuracyPct < 70 && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Weakest area: <b>{weakest.cognitiveSkill}</b> — {weakest.accuracyPct}% correct.
        </Typography>
      )}
      {review.skillBreakdown.map((s) => (
        <Box key={s.cognitiveSkill} sx={{ mb: 1.5 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography variant="body2">{s.cognitiveSkill}</Typography>
            <Typography variant="body2" color="text.secondary">
              {s.correct}/{s.attempted} · {s.accuracyPct}%
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={Math.min(100, Math.max(0, s.accuracyPct))}
            color={accuracyColor(s.accuracyPct)}
            sx={{ height: 8, borderRadius: 4 }}
          />
        </Box>
      ))}
    </Box>
  );
};

const LockedState: React.FC<{ onUpgradeClick?: () => void }> = ({ onUpgradeClick }) => (
  <Box sx={{ textAlign: 'center', py: 3 }}>
    <Lock sx={{ fontSize: 40, color: 'text.secondary', mb: 1 }} />
    <Typography variant="h6" sx={{ mb: 0.5 }}>
      See why you got each question wrong
    </Typography>
    <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: 460, mx: 'auto' }}>
      Unlock step-by-step explanations for every question, plus a breakdown of the
      skills costing you the most marks.
    </Typography>
    <Button variant="contained" size="large" onClick={onUpgradeClick}>
      Unlock full solutions
    </Button>
  </Box>
);

const SessionReviewPanel: React.FC<Props> = ({ sessionId, onUpgradeClick }) => {
  const [result, setResult] = useState<SessionReviewResult | null>(null);

  useEffect(() => {
    let cancelled = false;
    if (!sessionId) return undefined;
    setResult(null);
    fetchSessionReview(sessionId).then((r) => {
      if (!cancelled) setResult(r);
    });
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  return (
    <Paper sx={{ p: 3, mt: 3, borderRadius: 2 }}>
      {result === null && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress />
        </Box>
      )}

      {result?.state === 'locked' && <LockedState onUpgradeClick={onUpgradeClick} />}

      {result?.state === 'unauthorized' && (
        <Alert severity="warning">Please sign in again to view this review.</Alert>
      )}

      {result?.state === 'error' && <Alert severity="error">{result.message}</Alert>}

      {result?.state === 'ok' && (
        <>
          <Typography variant="h5" sx={{ mb: 2 }}>
            Detailed solutions
          </Typography>
          <WeakAreas review={result.review} />
          <Divider sx={{ mb: 2 }} />
          {result.review.questions.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No questions were recorded for this attempt.
            </Typography>
          ) : (
            result.review.questions.map((q, i) => (
              <QuestionRow key={q.questionId ?? i} q={q} n={i + 1} />
            ))
          )}
        </>
      )}
    </Paper>
  );
};

export default SessionReviewPanel;
